import React, {memo, SyntheticEvent, useEffect, useMemo} from 'react';
import MaterialInput from "../../components/MaterialInput";
import Button from "../../components/Button";
import OtpInput, {OTP_LENGTH} from "../../components/OtpInput";
import {API} from "../../types/API";
import Logo from "../../assets/icons/Logo";
import {AuthResponse} from "../../types/Responses/AuthResponse";
import {AxiosResponse} from "axios";
import {TEAnimation} from "tw-elements-react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useTranslation} from "react-i18next";
import Turnstile, {BoundTurnstileObject} from "react-turnstile";
import {startAuthentication} from '@simplewebauthn/browser';
import DropdownWithArrow, {DropdownItem} from "./DropdownWithArrow";
import UserAvatar from "../../components/UserAvatar";
import {useApplicationContext} from "../../types/contexts/ApplicationContext";
import TotalFXLogo from "../../assets/icons/TotalFXLogo";
import {setupReconnect} from "../../hooks/reconnect";
import {APILink} from "../../types/APILink";

export interface ENV {
    NODE_ENV: string;
    PUBLIC_URL: string;
    FAST_REFRESH: boolean;
    REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY: string;
    REACT_APP_TITLE: string;
    REACT_APP_DESCRIPTION: string;
    REACT_APP_LOGO: string;
}

const Login = () => {

    const app = useApplicationContext();
    const {t} = useTranslation();
    const emailRef = React.useRef<HTMLInputElement>(null);
    const passwordRef = React.useRef<HTMLInputElement>(null);
    const autoPasskeyLoginTriggered = React.useRef<boolean>(false);

    const [email, setEmail] = React.useState<string>('');
    const [password, setPassword] = React.useState<string>('');
    const [code, setCode] = React.useState<string>(' '.repeat(OTP_LENGTH));

    const [errors, setErrors] = React.useState<Record<string, string> | null>(null);
    const [isInProgress, setInProgress] = React.useState<boolean>(false);
    const [step, setStep] = React.useState<number>(1);
    const [isLoginByPasskey, setLoginByPasskey] = React.useState<boolean>(false);
    const [showExtraMargin, setShowExtraMargin] = React.useState<boolean>(false);
    const [siteKey, setsiteKey] = React.useState<string>('');
    const [logoImage, setLogoImage] = React.useState<string>('topfx');
    const [savedPasskeys, setSavedPasskeys] = React.useState<{
        email: string;
        name: string;
    }[]>([]);
    const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null);
    const [turnstileWidget, setTurnstileWidget] = React.useState<BoundTurnstileObject | null>(null);
    const setLocalEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.currentTarget.value);
    }

    const setLocalPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.currentTarget.value);
    }

    const setLocalCode = (value: string) => {
        setCode(value);
    }

    const clearTurnstileError = React.useCallback(() => {
        setErrors((prev) => {
            if (!prev)
                return prev;
            if (typeof prev['turnstile'] === "undefined")
                return prev;
            const updated: Record<string, string> = {...prev};
            delete updated['turnstile'];
            return Object.keys(updated).length ? updated : {};
        });
    }, []);

    const resetTurnstile = React.useCallback((bound?: BoundTurnstileObject | null) => {
        const target = bound ?? turnstileWidget;
        if (target) {
            try {
                target.reset();
            } catch (e) {
                // Widget already removed, ignore reset errors
            }
        }
        setTurnstileToken(null);
    }, [turnstileWidget]);

    const loginByPasskey = async (token: string, em?: string) => {
        const localEmail = em ?? email;
        const passkeyOptions = await API.getAuthPasskeyOptions(localEmail);
        const r = await startAuthentication({
            optionsJSON: passkeyOptions.data,
            useBrowserAutofill: false,
            verifyBrowserAutofillInput: false
        });
        const response = await API.passkeyLogin({...r, email: localEmail, token});
        const authResponse = response.data as AuthResponse & { name: string };
        if (authResponse.status) {
            if (authResponse.token) {
                const currentUser = {
                    email: localEmail,
                    name: authResponse.name,
                };
                if (localStorage.getItem('passkeys') ?? null) {
                    const storedPasskeys: {
                        email: string;
                        name: string
                    }[] = JSON.parse(atob(localStorage.getItem('passkeys') ?? '') ?? '[]');
                    if (!storedPasskeys.map(s => s.email).includes(currentUser.email)) {
                        storedPasskeys.push(currentUser);
                    }
                    localStorage.setItem('passkeys', btoa(JSON.stringify(storedPasskeys)));
                } else {
                    localStorage.setItem('passkeys', btoa(JSON.stringify([currentUser])));
                }
                localStorage.setItem('last_used_passkey', btoa(localEmail));

                window.location.replace('/app/admin/dashboard');
            }
            if (authResponse.isGoogle2FA) {
                changedStep(2);
            }
            return;
        }

        throw new Error(t('admin.login.failed', {defaultValue: 'Authentication failed'}));
    }

    const login = async (em?: string) => {
        setErrors({});
        const localErrors: Record<string, string> = {};
        if (email.length == 0 && !em) {
            localErrors['email'] = 'Email field is required';
        } else if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
            localErrors['email'] = 'Email format is not valid';
        }
        if (password.length == 0 && !isLoginByPasskey && !em) {
            localErrors['password'] = 'Password field is required';
        }

        if (Object.keys(localErrors).length) {
            setErrors(localErrors);
            return;
        }

        if (!turnstileToken) {
            setErrors({
                turnstile: t('admin.login.turnstile.required', {defaultValue: 'Please verify the Turnstile challenge and try again.'})
            });
            return;
        }

        setInProgress(true);
        const tokenToSend = turnstileToken;
        setTurnstileToken(null);
        try {
            if (isLoginByPasskey || em) {
                await loginByPasskey(tokenToSend, em);
            } else {
                const response = await API.login(email, password, tokenToSend);
                const authResponse = response.data as AuthResponse;

                if (authResponse.status) {
                    if (authResponse.token) {
                        console.log('logged in', localStorage);
                        window.location.replace('/app/admin/dashboard');
                    }
                    if (authResponse.isGoogle2FA) {
                        changedStep(2);
                    }
                }
            }
        } catch (error: any) {
            if (typeof error?.response !== 'undefined' && typeof error.response?.data !== "undefined" && typeof error.response.data.errors !== "undefined") {
                setErrors(error.response.data.errors);
            } else if (typeof error?.message === 'string') {
                setErrors({errors: error.message});
            } else {
                setErrors({errors: t('admin.login.unexpected', {defaultValue: 'Unable to login. Please try again.'})});
            }
        } finally {
            setInProgress(false);
            resetTurnstile();
        }
    }

    const handleLoginSubmit = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        login();
    }

    const check2FA = (e: SyntheticEvent) => {
        e.preventDefault();
        setErrors({});
        const errors: Record<string, string> = {};
        const sanitizedCode = code.replace(/\s/g, '');
        if (sanitizedCode.length !== OTP_LENGTH)
            errors['code'] = 'Code must be 6 digits';

        if (Object.keys(errors).length)
            setErrors(errors);

        if (!Object.keys(errors).length) {
            setInProgress(true);
            API.verify2fa(sanitizedCode, email).then((response: AxiosResponse) => {
                setInProgress(false);
                if (typeof response.data.token !== "undefined") {
                    localStorage.setItem('userToken', response.data.token);
                    localStorage.setItem('tokenExpiresAt', response.data.expires_at);
                    setupReconnect();
                    window.location.replace('/app/admin/dashboard');
                }
            }).catch((error: any) => {
                setInProgress(false);
                if (typeof error.response !== 'undefined' && typeof error.response.data !== "undefined" && typeof error.response.data.errors !== "undefined") {
                    setErrors(error.response.data.errors);
                }
                API.removeURL(`post${APILink.VERIFY_2FA}`);
            });
        }

    }

    const hasError = (key: string) => {
        return errors !== null && typeof errors[key] !== "undefined";
    }

    const getError = (key: string) => {
        return errors !== null && typeof errors[key] !== "undefined" ? errors[key] : ''
    }

    const changedStep = (newStep: number) => {
        setStep(newStep);
    }

    const startElement = () => {
        setShowExtraMargin(true);
    }

    const endElement = () => {
        setShowExtraMargin(false);
    }

    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const url = new URL(window.location.href);
        const keysToRemove: string[] = [];
        url.searchParams.forEach((_, key) => {
            if (key.startsWith('__cf_chl') || key.startsWith('cf_chl')) {
                keysToRemove.push(key);
            }
        });
        if (keysToRemove.length) {
            keysToRemove.forEach((key) => url.searchParams.delete(key));
            const params = url.searchParams.toString();
            const cleanUrl = `${url.pathname}${params ? `?${params}` : ''}${url.hash}`;
            window.history.replaceState(null, '', cleanUrl);
        }
    }, []);

    useEffect(() => {
        const env = process.env as unknown as ENV;
        const userToken = localStorage.getItem('userToken') ?? null;
        if (userToken)
            window.location.replace('/app/admin/dashboard');
        setLogoImage(env.REACT_APP_LOGO);
        setsiteKey(env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY);
        if (localStorage.getItem('passkeys') ?? null) {
            setSavedPasskeys(JSON.parse(atob(localStorage.getItem('passkeys') ?? '') ?? '[]'));
        }
    }, [app]);

    useEffect(() => {
        if (!app.isPWA || autoPasskeyLoginTriggered.current || isInProgress)
            return;
        const lastPasskey = localStorage.getItem('last_used_passkey');
        if (!lastPasskey || !turnstileToken)
            return;
        const decodedEmail = atob(lastPasskey ?? '');
        if (!decodedEmail.length)
            return;
        autoPasskeyLoginTriggered.current = true;
        login(decodedEmail);
    }, [app, turnstileToken, isInProgress]);

    const onClickLoginAsSelectedPasskey = (item: DropdownItem) => {
        if (item.value.length) {
            login(item.value);
        } else {
            setLoginByPasskey(true);
        }
    }

    const loginByPasskeyUI = useMemo(() => {
        if (isLoginByPasskey)
            return <label className="text-sm cursor-pointer text-gray-600"
                          onClick={() => setLoginByPasskey(false)}>enter by password</label>

        if (savedPasskeys.length === 0 && !isLoginByPasskey)
            return <label className="text-sm cursor-pointer text-gray-600"
                          onClick={() => setLoginByPasskey(true)}>enter by Passkey</label>

        if (savedPasskeys.length > 0) {
            let selected: string | null | DropdownItem = localStorage.getItem('last_used_passkey') ?? null;
            if (selected) {
                selected = atob(localStorage.getItem('last_used_passkey') ?? '');
                if (selected) {
                    const found = savedPasskeys.find(s => s.email === selected);
                    if (found) {
                        selected = {label: found.name, value: found.email};
                    }
                }
            }
            const items = [...savedPasskeys.map((p) => {
                return {label: p.name, value: p.email};
            })];
            items.push({label: 'Choose another account', value: ''})
            return <DropdownWithArrow items={items} label="Login as"
                                      defaultSelect={selected ? selected as DropdownItem : undefined}
                                      onSelect={onClickLoginAsSelectedPasskey}
                                      icon={<UserAvatar className="text-gray-900 dark:text-gray-500" image=""
                                                        size="xs"/>}/>
        }

        return <></>
    }, [isLoginByPasskey, savedPasskeys, onClickLoginAsSelectedPasskey, login, localStorage]);

    const getLogoUI = useMemo(() => {
        if (logoImage !== 'totalfx')
            return <Logo className="mb-8 h-12 inline-block"/>

        return <TotalFXLogo className="mb-8 h-12 inline-block"/>
    }, [logoImage]);

    return (
        <div className="bg-neutral-800 w-screen h-screen flex justify-center items-center">
            <div className="w-full max-w-sm bg-white p-6 text-black rounded-xl">
                <div className="text-center">
                    {getLogoUI}
                </div>
                <div className="relative">
                    <div className={`justify-center overflow-hidden ${step !== 2 ? 'flex' : 'hidden'}`}>
                        <TEAnimation toggle={step === -1} onStart={() => {
                            startElement();
                        }} onEnd={() => {
                            endElement();
                        }} animation="[slide-left_1s_ease-in-out]" start="manually"
                                     className={`text-center w-full z-10 ${showExtraMargin ? '-mr-[678px]' : ''}`}>
                            <form onSubmit={handleLoginSubmit}>
                                <h1 className="text-center text-xl mb-8 block">{t('admin.login.title')}</h1>
                                <MaterialInput ref={emailRef} type='email' name='email'
                                               placeholder={t('admin.login.email.placeholder')}
                                               onChange={setLocalEmail}
                                               required={true}
                                               relativeClassName="my-3"/>
                                {hasError('email') &&
                                    <label className="-mt-1 text-red-500 text-xs block">{getError('email')}</label>}
                                {!isLoginByPasskey ? <>
                                    <MaterialInput ref={passwordRef} type='password' name='password'
                                                   placeholder={t('admin.login.password.placeholder')}
                                                   onChange={setLocalPassword}
                                                   required={true}
                                                   relativeClassName="my-3"/>
                                    {hasError('password') &&
                                        <label
                                            className="-mt-1 text-red-500 text-xs block">{getError('password')}</label>}
                                </> : <input
                                    type="text"
                                    id="webauthn-autofill"
                                    autoComplete="webauthn"
                                    value=""
                                    readOnly
                                    style={{position: 'absolute', opacity: 0, height: 0, width: 0}}
                                />}
                                {hasError('errors') &&
                                    <label
                                        className="-mt-1 text-red-700 p-3 text-xs block text-center">{getError('errors')}</label>}
                                {hasError('turnstile') &&
                                    <label
                                        className="-mt-1 text-red-700 p-3 text-xs block text-center">{getError('turnstile')}</label>}

                                <div className="flex my-4 items-center justify-center">
                                    {loginByPasskeyUI}
                                </div>

                                <Turnstile
                                    appearance="always"
                                    refreshExpired="auto"
                                    className="flex justify-center items-center mt-2"
                                    theme="light"
                                    sitekey={siteKey}
                                    responseField={false}
                                    onLoad={(_, bound) => setTurnstileWidget(bound)}
                                    onVerify={(token, bound) => {
                                        setTurnstileWidget(bound);
                                        setTurnstileToken(token);
                                        clearTurnstileError();
                                    }}
                                    onExpire={(_, bound) => resetTurnstile(bound)}
                                    onError={(_, bound) => resetTurnstile(bound)}
                                />
                                <Button name={t('admin.login.submit_button')}
                                        withProgressBar={true}
                                        type="submit"
                                        isInProgress={isInProgress}
                                        className={`bg-default-btn-color text-white`}
                                        relativeClassName="mt-6"/>
                            </form>
                        </TEAnimation>
                    </div>
                    <div className={`justify-center overflow-hidden ${step == 2 ? 'flex' : 'hidden'}`}>
                        <FontAwesomeIcon
                            className={`absolute left-0 cursor-pointer mt-2 z-20 ${!showExtraMargin ? '' : 'hidden'}`}
                            icon={solid("arrow-left")} onClick={() => {
                            changedStep(-1)
                        }}/>
                        <TEAnimation toggle={step === 2} onStart={() => {
                            startElement();
                        }} onEnd={() => {
                            endElement();
                        }} animation="[slide-right_1s_ease-in-out]" start="manually"
                                     className={`text-center w-full z-10 ${showExtraMargin ? '-ml-[678px]' : ''}`}>
                            <form onSubmit={check2FA} action="#">
                                <h1 className="text-center text-xl mb-8 block">{t('admin.login.two_fa.title')}</h1>
                                <OtpInput value={code}
                                          onChange={setLocalCode}
                                          disabled={isInProgress}
                                          error={hasError('code')}
                                          className="my-3"/>
                                {hasError('code') &&
                                    <label className="-mt-1 text-red-500 text-xs block">{getError('code')}</label>}
                                <Button type="submit"
                                        name={t('admin.login.verify_button')}
                                        withProgressBar={true}
                                        isInProgress={isInProgress}
                                        className={`bg-default-btn-color text-white`}
                                        relativeClassName="mt-6"/>
                                <button className="mt-4 text-black text-xs" onClick={() => {
                                    changedStep(-1)
                                }}>{t('admin.login.back')}
                                </button>
                            </form>
                        </TEAnimation>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default memo(Login);
